/**
 * PitchBlackHero — the star of the Pitch Black event skin on Home.
 * A frosted liquid-glass hero with the Darkrai artwork, a neon
 * "MEGA EVOLUTION: PITCH BLACK" headline, and a LIVE countdown to the
 * store drop (Sat 09/12, doors 10:00 AM local). Renders only while the
 * Pitch Black theme is active (parent gates on `active`).
 *
 * CSS-only motion (float + eye-glow pulse) so there's no animation-lib
 * dependency; honors prefers-reduced-motion via the scoped stylesheet.
 */
import { useEffect, useState } from "react";

const TARGET = new Date("2026-09-12T10:00:00").getTime();

const pad = (n) => String(n).padStart(2, "0");

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const done = diff <= 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { done, days, hours, mins, secs };
}

const TimeCell = ({ value, label, testid }) => (
  <div className="flex flex-col items-center" data-testid={testid}>
    <span
      className="text-2xl sm:text-3xl font-black tabular-nums leading-none text-white px-2.5 py-1.5 rounded-lg"
      style={{
        fontFamily: "'Five Below Gothic', Chivo, sans-serif",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(236,0,140,0.4)",
        boxShadow: "0 0 12px rgba(236,0,140,0.3), inset 0 1px 1px rgba(255,255,255,0.15)",
        textShadow: "0 0 10px rgba(236,0,140,0.6)",
      }}
    >
      {value}
    </span>
    <span className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A3E635]">
      {label}
    </span>
  </div>
);

export default function PitchBlackHero({ active, onCta }) {
  const { done, days, hours, mins, secs } = useCountdown(TARGET);
  if (!active) return null;

  return (
    <section className="pb-1" data-testid="pitch-black-hero">
      <div
        className="relative overflow-hidden rounded-3xl"
        style={{
          background: "linear-gradient(135deg, rgba(30,4,30,0.85) 0%, rgba(6,6,10,0.92) 60%, rgba(10,20,6,0.85) 100%)",
          border: "1px solid rgba(236,0,140,0.4)",
          boxShadow: "0 10px 40px rgba(0,0,0,0.8), 0 0 26px rgba(236,0,140,0.22), inset 0 1px 1px rgba(255,255,255,0.12)",
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
        }}
      >
        {/* Darkrai — floats on the right, bleeds off the card edge */}
        <img
          src="/darkrai.webp"
          alt="Darkrai"
          className="pb-hero-darkrai absolute -right-6 -top-4 h-[150%] w-auto object-contain pointer-events-none select-none"
          style={{ maskImage: "linear-gradient(to left, #000 55%, transparent 100%)", WebkitMaskImage: "linear-gradient(to left, #000 55%, transparent 100%)" }}
          aria-hidden
          data-testid="pitch-black-hero-darkrai"
        />

        <div className="relative z-10 p-4 sm:p-5 max-w-[70%]">
          <span
            className="inline-block text-[10px] font-black uppercase tracking-[0.22em] px-2.5 py-1 rounded-full"
            style={{ background: "rgba(163,230,53,0.15)", color: "#A3E635", border: "1px solid rgba(163,230,53,0.5)" }}
          >
            ⚡ The Big Drop
          </span>
          <h2
            className="mt-2.5 text-2xl sm:text-3xl font-black uppercase italic leading-[0.95]"
            style={{
              fontFamily: "'Five Below Gothic', Chivo, sans-serif",
              backgroundImage: "linear-gradient(90deg, #EC008C 0%, #FDE047 50%, #A3E635 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              filter: "drop-shadow(0 0 14px rgba(236,0,140,0.5))",
            }}
            data-testid="pitch-black-hero-title"
          >
            Mega Evolution<br />Pitch Black
          </h2>

          {done ? (
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-black uppercase tracking-wide"
              style={{ background: "rgba(236,0,140,0.2)", color: "#FDE047", border: "1px solid rgba(236,0,140,0.6)" }}
              data-testid="pitch-black-hero-live"
            >
              ⚡ Drop is live — let's go!
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 sm:gap-3" data-testid="pitch-black-hero-countdown">
              <TimeCell value={days} label="days" testid="pb-cd-days" />
              <span className="text-xl font-black text-[#EC008C] -mt-3">:</span>
              <TimeCell value={pad(hours)} label="hrs" testid="pb-cd-hrs" />
              <span className="text-xl font-black text-[#EC008C] -mt-3">:</span>
              <TimeCell value={pad(mins)} label="min" testid="pb-cd-min" />
              <span className="text-xl font-black text-[#EC008C] -mt-3">:</span>
              <TimeCell value={pad(secs)} label="sec" testid="pb-cd-sec" />
            </div>
          )}

          {onCta && (
            <button
              type="button"
              onClick={onCta}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-extrabold text-white active:scale-[0.97] transition-transform"
              style={{
                background: "linear-gradient(90deg, #EC008C 0%, #A855F7 60%, #22D3EE 100%)",
                boxShadow: "0 0 22px rgba(236,0,140,0.55)",
              }}
              data-testid="pitch-black-hero-cta"
            >
              Rally the crew →
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pb-hero-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-10px) rotate(1.2deg); }
        }
        .pb-hero-darkrai { animation: pb-hero-float 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pb-hero-darkrai { animation: none; }
        }
      `}</style>
    </section>
  );
}
